import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { format, isToday } from 'date-fns';
import toast from 'react-hot-toast';

const JOBS_KEY = ['companyJobs'];

export const useJobs = () => {
    const queryClient = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: JOBS_KEY,
        queryFn: () => api.get('/company/jobs').then(res => res.data.data || []),
        staleTime: 1000 * 60 * 2,
    });

    const deleteMutation = useMutation({
        mutationFn: (jobId) => api.delete(`/company/jobs/${jobId}`),
        onSuccess: () => {
            toast.success('Job deleted successfully');
            queryClient.invalidateQueries(JOBS_KEY);
        },
        onError: () => toast.error('Failed to delete job'),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, ...data }) => api.put(`/company/jobs/${id}`, data),
        onSuccess: () => {
            toast.success('Job updated successfully');
            queryClient.invalidateQueries(JOBS_KEY);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to update job';
            toast.error(message);
        },
    });

    const jobs = (Array.isArray(data) ? data : []).map(job => {
        const quote = job.quote || {};
        const clientName = `${quote.first_name || ''} ${quote.last_name || ''}`.trim() || 'Unknown Client';

        const dateStr = job.start_date?.split('T')[0] || new Date().toISOString().split('T')[0];
        const timeStr = job.start_time?.split(':').slice(0, 2).join(':') || '00:00';
        const fullDate = `${dateStr}T${timeStr}:00`;
        const scheduledDate = new Date(fullDate);

        return {
            id: job.id,
            client: clientName,
            clientSubtitle: `${quote.first_name || ''} ${quote.last_name || ''}`,
            jobNumber: job.job_number,
            title: quote.service?.name || 'Survey Job',
            property: job.location || quote.boat_location || 'No location',
            schedule: format(scheduledDate, 'MMMM d, yyyy'),
            status: isToday(scheduledDate) ? 'Today' : 'Scheduled',
            total: 'USD 0.00',
            raw: job,
        };
    });

    return {
        jobs,
        isLoading,
        error,
        deleteJob: deleteMutation.mutate,
        isDeleting: deleteMutation.isPending,
        updateJob: updateMutation.mutate,
        isUpdating: updateMutation.isPending,
    };
};